import {
  Button,
  Combobox,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  MultiSelect,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { EmailField } from "@tellescope/forms";
import { useInviteUserForm, type UseInviteUserFormProps } from "./useInviteUserForm";

export type InviteUserDialogContentsProps = UseInviteUserFormProps;

export const InviteUserDialogContents = ({ roleOptions, tagOptions, onSuccess }: InviteUserDialogContentsProps) => {
  const { t } = useTranslation("settings");
  const { inviteUserForm, inviteRoleOptions, inviteTagOptions, selectedInviteRoleOption } = useInviteUserForm({
    roleOptions,
    tagOptions,
    onSuccess,
  });

  return (
    <DialogContent divided>
      <form {...inviteUserForm.getFormProps()}>
        <DialogHeader className="p-0 space-y-2">
          <DialogTitle className="px-6 pt-4">{t("Invite User")}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 px-6 py-4">
          <div className="space-y-2 col-span-2">
            <Label htmlFor="invite-email">{t("Email")}</Label>
            <EmailField
              id="invite-email"
              value={inviteUserForm.values.email}
              onChange={(newVal) => inviteUserForm.setFieldValue("email", newVal ?? "")}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="invite-fname">{t("First Name")}</Label>
            <Input id="invite-fname" {...inviteUserForm.getFieldProps("fname")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="invite-lname">{t("Last Name")}</Label>
            <Input id="invite-lname" {...inviteUserForm.getFieldProps("lname")} />
          </div>
          <div className="space-y-2 col-span-2">
            <Label htmlFor="invite-internalDisplayName">{t("Display Name (team facing)")}</Label>
            <Input id="invite-internalDisplayName" {...inviteUserForm.getFieldProps("internalDisplayName")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="invite-role">{t("Role")}</Label>
            <Combobox
              id="invite-role"
              value={selectedInviteRoleOption}
              onChange={(option) => inviteUserForm.setFieldValue("role", option?.value ?? "")}
              options={inviteRoleOptions}
              placeholder={t("Non-Admin")}
              searchPlaceholder={t("Search roles...")}
              emptyText={t("No roles found")}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="invite-tags">{t("Tags")}</Label>
            <MultiSelect
              id="invite-tags"
              options={inviteTagOptions}
              {...inviteUserForm.getCustomFieldProps("tags")}
              placeholder={t("No tags set")}
              searchPlaceholder={t("Search tags or enter a value...")}
              emptyText={t("No tags found")}
              creator={{
                render: (inputValue) => t('Add "{{value}}"', { value: inputValue }),
                isValidNewOption: (inputValue, selectedValues) =>
                  !!inputValue.trim() && !selectedValues.includes(inputValue.trim()),
                transform: (inputValue) => inputValue.trim(),
                onCreate: (tag) => {
                  inviteUserForm.setFieldValue("tags", [...inviteUserForm.values.tags, tag.value]);
                },
              }}
              showAsBadges={false}
              className="h-8 w-full"
            />
          </div>
        </div>
        <DialogFooter className="bg-gray-100 rounded-b-md">
          <DialogClose
            onClick={() => {
              inviteUserForm.resetForm();
            }}
          >
            <Button variant="outline">{t("Cancel")}</Button>
          </DialogClose>
          <Button type="submit">{t("Send Invite")}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
};
